// MyVehiclesPage.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getMyVehicles } from '../services/vehicleService';
import { useAuth } from '../context/AuthContext';

const MyVehiclesPage = () => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { currentUser } = useAuth();

    useEffect(() => {
        const fetchVehicles = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await getMyVehicles();

                if (response.success) {
                    setVehicles(response.vehicles);
                } else {
                    setError(response.message || 'Araçlar getirilirken bir hata oluştu');
                }
            } catch (error) {
                console.error("Araçlar yüklenirken bir hata oluştu:", error);
                setError('Araçlar yüklenirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
            } finally {
                setLoading(false);
            }
        };

        if (currentUser) {
            fetchVehicles();
        } else {
            setLoading(false);
        }
    }, [currentUser]);

    // Cloudinary URL'sini boyutlandırmak için yardımcı fonksiyon
    // Cloudinary URL'sini boyutlandırmak için yardımcı fonksiyon
    const getOptimizedImageUrl = (imageUrl, width = 600, height = 400) => {
        // Eğer resim yoksa yerel default resmi döndür
        if (!imageUrl) return "/uploads/vehicles/default.jpg";

        // Eğer Cloudinary URL'si ise optimize et (Cloudinary default URL'sini ENGELLE)
        if (imageUrl.includes('cloudinary.com')) {
            if (imageUrl.includes('default_zxlbnq.webp')) {
                return "/uploads/vehicles/default.jpg";
            }

            try {
                const parts = imageUrl.split('/upload/');
                if (parts.length !== 2) return imageUrl;

                return `${parts[0]}/upload/w_${width},h_${height},c_fill,q_auto/${parts[1]}`;
            } catch (error) {
                console.error('URL işleme hatası:', error);
                return imageUrl;
            }
        }

        // Eğer yerel URL değilse ve tam URL formatında değilse
        if (!imageUrl.startsWith('http')) {
            return `${imageUrl}`;
        }

        return imageUrl;
    };

    // Araç için en iyi resmi seç
    const getVehicleImage = (vehicle) => {
        if (!vehicle) return getOptimizedImageUrl(null);

        if (vehicle.images && vehicle.images.length > 0) {
            return getOptimizedImageUrl(vehicle.images[0]);
        }

        if (vehicle.imageUrl) {
            return getOptimizedImageUrl(vehicle.imageUrl);
        }

        return getOptimizedImageUrl(null);
    };

    const formatPrice = (price) => {
        if (!price) return "0";
        return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    if (loading) {
        return (
            <Container className="my-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
            </Container>
        );
    }

    if (!currentUser) {
        return (
            <Container className="my-5">
                <Alert variant="warning">
                    Araçlarınızı görmek için giriş yapmalısınız.
                    <Link to="/login" className="ms-3 btn btn-primary btn-sm">Giriş Yap</Link>
                </Alert>
            </Container>
        );
    }

    return (
        <Container className="my-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>Araçlarım</h1>
                <Link to="/vehicles/add" className="btn btn-primary">
                    <i className="bi bi-plus-circle me-2"></i>
                    Yeni Araç Ekle
                </Link>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {vehicles.length === 0 ? (
                <Alert variant="info">
                    Henüz bir araç ilanınız bulunmamaktadır.
                    <Link to="/vehicles/add" className="ms-3 btn btn-primary btn-sm">Araç Ekle</Link>
                </Alert>
            ) : (
                <Row>
                    {vehicles.map(vehicle => (
                        <Col md={4} key={vehicle._id} className="mb-4">
                            <Card className="h-100 shadow-sm">
                                <div style={{ height: '200px', overflow: 'hidden' }}>
                                    <Card.Img
                                        variant="top"
                                        src={getVehicleImage(vehicle)}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = getOptimizedImageUrl(null);
                                        }}
                                        alt={vehicle.title || `${vehicle.brand} ${vehicle.model}`}
                                        style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                                    />
                                </div>
                                <Card.Body className="d-flex flex-column">
                                    <Card.Title>{vehicle.title || `${vehicle.brand} ${vehicle.model}`}</Card.Title>
                                    <Card.Text>
                                        <strong>{formatPrice(vehicle.price)} ₺</strong>
                                    </Card.Text>
                                    <Card.Text>
                                        {vehicle.brand} {vehicle.model} - {vehicle.year}
                                    </Card.Text>
                                    <Card.Text>
                                        {vehicle.location?.city && vehicle.location?.district
                                            ? `${vehicle.location.city}, ${vehicle.location.district}`
                                            : 'Konum bilgisi yok'}
                                    </Card.Text>

                                    <div className="mt-auto">
                                        <div className="d-grid gap-2 mb-2">
                                            <Link to={`/vehicles/${vehicle._id}`} className="btn btn-primary">
                                                Görüntüle
                                            </Link>
                                        </div>
                                        <div className="d-grid">
                                            <Link to={`/vehicles/edit/${vehicle._id}`} className="btn btn-outline-secondary">
                                                <i className="bi bi-pencil me-2"></i>
                                                Düzenle
                                            </Link>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </Container>
    );
};

export default MyVehiclesPage;